import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useConnect, useDisconnect, useSignTypedData, useSwitchChain } from "wagmi";
import { base } from "wagmi/chains";
import { recoverX402ReviewSigner, x402ReviewTypedData } from "@boon/x402-route";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { shortAddr } from "../lib/api";
import {
  fetchX402Recognitions,
  fetchX402Route,
  publishX402Review,
  type X402RouteResponse,
} from "../lib/x402Discovery";
import { readX402ReviewSigningPayload } from "../lib/x402ReviewSigning";

type PayloadResult =
  | { payload: ReturnType<typeof readX402ReviewSigningPayload>; error: null }
  | { payload: null; error: string };

export function X402ReviewSignPage() {
  const [initial] = useState<PayloadResult>(() => {
    try {
      return { payload: readX402ReviewSigningPayload(window.location.hash), error: null };
    } catch (cause) {
      return { payload: null, error: cause instanceof Error ? cause.message : String(cause) };
    }
  });
  const payload = initial.payload;
  const [route, setRoute] = useState<X402RouteResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validating, setValidating] = useState(Boolean(payload));
  const [confirmedUse, setConfirmedUse] = useState(false);
  const [status, setStatus] = useState<"idle" | "connecting" | "signing" | "publishing" | "published">("idle");
  const [error, setError] = useState<string | null>(null);
  const { address, chainId, isConnected } = useAccount();
  const { connectors, connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const walletConnector = useMemo(
    () => connectors.find((connector) => /metamask/i.test(connector.name) || connector.id === "metaMask" || connector.type === "metaMask") ??
      connectors.find((connector) => connector.type === "injected") ?? connectors[0],
    [connectors],
  );

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }, []);

  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    setValidating(true);
    Promise.all([fetchX402Route(payload.review.routeId), fetchX402Recognitions(payload.review.routeId)])
      .then(([routeResult, recognitionResult]) => {
        if (cancelled) return;
        if (routeResult.contract.toLowerCase() !== payload.domain.verifyingContract.toLowerCase()) {
          throw new Error("The signing contract does not match Boon’s indexed deployment.");
        }
        const recognition = recognitionResult.routedBoons.find((row) =>
          row.txHash.toLowerCase() === payload.review.recognitionTxHash.toLowerCase() &&
          Number(row.logIndex) === payload.review.recognitionLogIndex,
        );
        if (!recognition) throw new Error("The cited routed Boon was not found for this endpoint.");
        if (recognition.tipper.toLowerCase() !== payload.expectedSigner.toLowerCase()) {
          throw new Error("The required signer did not send the cited routed Boon.");
        }
        const alreadyReviewed = routeResult.route.reviews.some((review) =>
          review.evidenceKind === "boon_backed" &&
          review.recognition.transactionHash.toLowerCase() === payload.review.recognitionTxHash.toLowerCase() &&
          Number(review.recognition.logIndex) === payload.review.recognitionLogIndex,
        );
        if (alreadyReviewed) throw new Error("This routed Boon already has a signed review.");
        setRoute(routeResult);
        setValidationError(null);
      })
      .catch((cause) => {
        if (!cancelled) setValidationError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => { cancelled = true; };
  }, [payload]);

  async function connectWallet() {
    if (!walletConnector) {
      setError("No compatible EVM wallet was found. Open this page with a Base-compatible wallet.");
      return;
    }
    setStatus("connecting");
    setError(null);
    try {
      await connectAsync({ connector: walletConnector });
      setStatus("idle");
    } catch (cause) {
      setStatus("idle");
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function signAndPublish() {
    if (!payload || !route || !address || !confirmedUse) return;
    setError(null);
    try {
      if (Math.floor(Date.now() / 1_000) > payload.expiresAt) throw new Error("This signing link expired. Run the CLI command again.");
      if (address.toLowerCase() !== payload.expectedSigner.toLowerCase()) {
        throw new Error(`Switch to ${payload.expectedSigner} in your wallet before signing.`);
      }
      if (chainId !== base.id) await switchChainAsync({ chainId: base.id });
      const typedData = x402ReviewTypedData(payload.review, payload.domain);
      setStatus("signing");
      const signature = await signTypedDataAsync({ account: address, ...typedData });
      const recovered = await recoverX402ReviewSigner(payload.review, signature, payload.domain);
      if (recovered.toLowerCase() !== payload.expectedSigner.toLowerCase()) {
        throw new Error(`The signature recovered ${recovered}, not the recorded recognizer.`);
      }
      setStatus("publishing");
      await publishX402Review(payload.review, signature);
      setStatus("published");
    } catch (cause) {
      setStatus("idle");
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const description = route?.route.description;
  const blocked = initial.error || validationError;

  return (
    <>
      <Nav current="x402" />
      <main className="px-6 md:px-10 py-12 md:py-20 max-w-5xl mx-auto">
        <Link to="/x402" className="btn-mono text-xs text-muted hover:text-ink">← Recognized endpoints</Link>
        <header className="mt-7 max-w-4xl">
          <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">CLI prepared review</p>
          <h1 className="mt-3 text-4xl md:text-6xl font-display tracking-tight leading-[0.98]">Sign with your recognizer wallet</h1>
          <p className="mt-5 text-base text-muted leading-relaxed max-w-3xl">
            Boon loaded this exact review from the CLI link. The fragment has already been removed from the address bar. Review every field before signing in your wallet.
          </p>
        </header>

        {blocked ? (
          <Panel title="This review cannot be signed." detail={blocked} />
        ) : !payload || validating || !route ? (
          <Panel title="Verifying the routed Boon…" />
        ) : status === "published" ? (
          <section className="mt-10 border border-faint bg-paper rounded-md p-6 md:p-8" role="status" aria-live="polite">
            <p className="btn-mono text-xs uppercase tracking-[0.14em] text-olive-deep">Review published</p>
            <h2 className="mt-3 text-3xl font-display">Attached to the recorded Boon.</h2>
            <blockquote className="mt-6 border-l-4 border-olive pl-5 text-xl md:text-2xl font-display leading-snug">“{payload.review.reviewText}”</blockquote>
            <Link className="mt-6 inline-flex min-h-11 items-center btn-mono text-sm text-olive-deep hover:underline" to={`/x402/routes/${payload.review.routeId}`}>
              View endpoint review →
            </Link>
          </section>
        ) : (
          <section className="mt-10 border border-faint bg-paper rounded-md overflow-hidden">
            <div className="p-6 md:p-8">
              <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Recognized endpoint</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-display tracking-tight">{description?.summary ?? `Route ${payload.review.routeId.slice(0, 10)}…`}</h2>
              {description && <p className="mt-3 btn-mono text-sm text-olive-deep break-all">{description.route.method} {description.route.publicEndpointUrl}</p>}
              <blockquote className="mt-7 border-l-4 border-olive pl-5 text-xl md:text-3xl font-display leading-snug">“{payload.review.reviewText}”</blockquote>
            </div>
            <dl className="grid gap-px bg-faint border-y border-faint md:grid-cols-2">
              <Fact label="Required signer" value={payload.expectedSigner} />
              <Fact label="Recognition receipt" value={`${payload.review.recognitionTxHash}:${payload.review.recognitionLogIndex}`} />
            </dl>
            <div className="p-6 md:p-8">
              <label className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed">
                <input className="mt-1" type="checkbox" checked={confirmedUse} onChange={(event) => setConfirmedUse(event.target.checked)} />
                <span>I used this endpoint through x402 before sending the cited recognition.</span>
              </label>
              <p className="mt-4 text-xs text-muted leading-relaxed">Publishing moves no funds and burns no additional $BOON. The review and wallet signature become public.</p>

              {!isConnected ? (
                <button className="btn btn-primary mt-6" type="button" onClick={() => void connectWallet()} disabled={status === "connecting"}>
                  {status === "connecting" ? "Connecting…" : walletConnector ? `Connect ${walletConnector.name}` : "Connect wallet"}
                </button>
              ) : address?.toLowerCase() !== payload.expectedSigner.toLowerCase() ? (
                <div className="mt-6">
                  <p className="text-sm text-danger" role="alert">Connected as {address ? shortAddr(address) : "another wallet"}. Use {shortAddr(payload.expectedSigner)}.</p>
                  <button className="btn btn-ghost mt-3" type="button" onClick={() => void disconnectAsync()}>Disconnect wallet</button>
                </div>
              ) : (
                <button className="btn btn-primary mt-6" type="button" onClick={() => void signAndPublish()} disabled={!confirmedUse || status === "signing" || status === "publishing"}>
                  {status === "signing" ? "Review signature in your wallet…" : status === "publishing" ? "Publishing…" : "Sign and publish review"}
                </button>
              )}
              {error && <p className="mt-4 text-sm text-danger" role="alert">{error}</p>}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function Panel({ title, detail }: { title: string; detail?: string }) {
  return <section className="mt-10 border-y border-faint py-12 text-center" role={detail ? "alert" : "status"} aria-live="polite"><p className="font-display text-2xl">{title}</p>{detail && <p className="mt-3 text-sm text-muted">{detail}</p>}</section>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="bg-paper px-6 py-5 min-w-0"><dt className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</dt><dd className="mt-2 btn-mono text-xs break-all">{value}</dd></div>;
}
